// @ts-check
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Menu } from "primereact/menu"
import Button from "../../../../../components/atoms/Button.atom"
import Modal from "../../../../../components/atoms/Modal.atom"
import { severities } from "../../../../../consts/common.const"
import useToast from "../../../../../hooks/useToast.hook"
import {
  deleteChat,
  listChats,
} from "../../../../../services/estimations/estimationAgentChat.service"
import { clsx } from "../../../../../utils/strings.utils"
import ChatTitleModal from "./ChatTitleModal"
import { LOCAL_NEW_CHAT_ID } from "./estimationAgentChat.constants"

const CHAT_HISTORY_SKELETON_COUNT_KEY =
  "aik.estimationAgent.quickV3.chatHistorySkeletonCount"

const DEFAULT_SKELETON_COUNT = 12

/**
 * @returns {number}
 */
function readStoredSkeletonCount() {
  if (typeof window === "undefined") return DEFAULT_SKELETON_COUNT
  try {
    const raw = window.localStorage.getItem(CHAT_HISTORY_SKELETON_COUNT_KEY)
    if (raw == null || raw === "") return DEFAULT_SKELETON_COUNT
    const n = parseInt(raw, 10)
    if (!Number.isFinite(n) || n < 1) return DEFAULT_SKELETON_COUNT
    return n
  } catch {
    return DEFAULT_SKELETON_COUNT
  }
}

/**
 * @param {number} chatCount
 */
function persistSkeletonCountForNextLoad(chatCount) {
  if (typeof window === "undefined") return
  try {
    const next = Math.max(1, chatCount + 1)
    window.localStorage.setItem(CHAT_HISTORY_SKELETON_COUNT_KEY, String(next))
  } catch {
    // ignore quota / private mode
  }
}

/**
 * @typedef {object} ChatHistorySidebarProps
 * @property {boolean} open
 * @property {() => void} onClose
 * @property {string | null} activeChatId
 * @property {boolean} activeChatIsNuevo
 * @property {(chatId: string) => void | Promise<void>} onSelectChat
 * @property {(chatId: string, title: string) => void} [onChatTitleUpdated]
 * @property {(chatId: string) => void | Promise<void>} [onChatDeleted]
 */

/**
 * @param {ChatHistorySidebarProps} props
 */
export default function ChatHistorySidebar({
  open,
  onClose,
  activeChatId,
  activeChatIsNuevo,
  onSelectChat,
  onChatTitleUpdated,
  onChatDeleted,
}) {
  const { setMessage } = useToast()
  const [items, setItems] = useState(
    /** @type {{ id: string; title: string; user: number }[]} */ ([])
  )
  const [loadError, setLoadError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [skeletonCount, setSkeletonCount] = useState(DEFAULT_SKELETON_COUNT)

  const [titleModalOpen, setTitleModalOpen] = useState(false)
  const [titleEditChatId, setTitleEditChatId] = useState(
    /** @type {string | null} */ (null)
  )
  const [titleEditInitialTitle, setTitleEditInitialTitle] = useState("")

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteChatId, setDeleteChatId] = useState(
    /** @type {string | null} */ (null)
  )
  const [deleteBusy, setDeleteBusy] = useState(false)

  const chatRowMenuRef = useRef(/** @type {import("primereact/menu").Menu | null} */ (
    null
  ))
  const menuActionRef = useRef(
    /** @type {{ id: string; title: string }} */ ({
      id: "",
      title: "",
    })
  )

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadError(false)
    setSkeletonCount(readStoredSkeletonCount())
    setLoading(true)
    listChats().then((res) => {
      if (cancelled) return
      setLoading(false)
      if (!res.success) {
        setLoadError(true)
        setItems([])
        return
      }
      persistSkeletonCountForNextLoad(res.chats.length)
      setItems(res.chats)
    })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    /** @param {KeyboardEvent} e */
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, onClose])

  const displayItems = useMemo(() => {
    const list = items
      .map((i) => ({ ...i }))
      .filter((i) => i.id !== LOCAL_NEW_CHAT_ID)

    if (activeChatIsNuevo && activeChatId) {
      const idx = list.findIndex((i) => i.id === activeChatId)
      if (idx === -1) {
        list.unshift({ id: activeChatId, title: "", user: 0 })
      } else if (idx > 0) {
        const [row] = list.splice(idx, 1)
        list.unshift(row)
      }
    }

    list.unshift({ id: LOCAL_NEW_CHAT_ID, title: "", user: 0 })
    return list
  }, [items, activeChatId, activeChatIsNuevo])

  /**
   * @param {{ id: string; title: string; user: number }} item
   */
  const rowLabel = (item) => {
    if (item.id === LOCAL_NEW_CHAT_ID) return "Nuevo chat"
    const t = item.title?.trim()
    if (item.id === activeChatId && !t && activeChatIsNuevo) return "Nuevo chat"
    if (!t) return "Chat sin título"
    return t
  }

  /**
   * @param {{ id: string; title: string; user: number }} item
   */
  const isItemActive = (item) => {
    if (activeChatId && item.id === activeChatId) return true
    if (!activeChatId && item.id === LOCAL_NEW_CHAT_ID && activeChatIsNuevo)
      return true
    return false
  }

  const openTitleModal = useCallback(() => {
    const { id, title } = menuActionRef.current
    if (!id) return
    setTitleEditChatId(id)
    setTitleEditInitialTitle(typeof title === "string" ? title : "")
    setTitleModalOpen(true)
  }, [])

  const openDeleteModal = useCallback(() => {
    const { id } = menuActionRef.current
    if (!id) return
    setDeleteChatId(id)
    setDeleteModalOpen(true)
  }, [])

  const chatRowMenuItems = useMemo(
    () => [
      {
        label: "Editar",
        icon: "pi pi-pencil",
        command: openTitleModal,
      },
      {
        label: "Eliminar",
        icon: "pi pi-trash",
        command: openDeleteModal,
      },
    ],
    [openTitleModal, openDeleteModal]
  )

  const handleTitleSaved = useCallback(
    /** @param {string} nextTitle */
    (nextTitle) => {
      if (!titleEditChatId) return

      setItems((prev) =>
        prev.map((row) =>
          row.id === titleEditChatId ? { ...row, title: nextTitle } : row
        )
      )
      onChatTitleUpdated?.(titleEditChatId, nextTitle)
    },
    [titleEditChatId, onChatTitleUpdated]
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteChatId) return
    setDeleteBusy(true)
    const res = await deleteChat(deleteChatId)
    setDeleteBusy(false)
    if (!res.success) {
      setMessage(
        severities.error,
        "No se pudo eliminar",
        "Inténtalo de nuevo en unos segundos.",
        4000,
        undefined,
        null,
        null
      )
      return
    }
    setItems((prev) => prev.filter((row) => row.id !== deleteChatId))
    setDeleteModalOpen(false)
    const removed = deleteChatId
    setDeleteChatId(null)
    const pending = onChatDeleted?.(removed)
    if (pending != null && typeof pending.catch === "function") {
      pending.catch(() => {})
    }
  }, [deleteChatId, onChatDeleted, setMessage])

  if (!open) return null

  /**
   * @param {{ id: string; title: string; user: number }} item
   */
  const renderRowInner = (item) => {
    const label = rowLabel(item)
    const active = isItemActive(item)

    if (item.id === LOCAL_NEW_CHAT_ID) {
      return (
        <button
          type="button"
          className={clsx(
            "estimation-agent-quick-v3__history-item",
            active && "estimation-agent-quick-v3__history-item--active"
          )}
          onClick={() => {
            const pending = onSelectChat(item.id)
            if (pending != null && typeof pending.catch === "function") {
              pending.catch(() => {})
            }
          }}
        >
          {label}
        </button>
      )
    }

    return (
      <div
        className={clsx(
          "estimation-agent-quick-v3__history-item-row",
          active && "estimation-agent-quick-v3__history-item-row--active"
        )}
      >
        <button
          type="button"
          className="estimation-agent-quick-v3__history-item"
          onClick={() => {
            const pending = onSelectChat(item.id)
            if (pending != null && typeof pending.catch === "function") {
              pending.catch(() => {})
            }
          }}
        >
          {label}
        </button>
        <div
          className="estimation-agent-quick-v3__history-item-actions"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <span
            className="estimation-agent-quick-v3__history-item-menu-trigger"
            role="button"
            tabIndex={0}
            aria-label="Acciones del chat"
            onClick={(e) => {
              e.stopPropagation()
              menuActionRef.current = {
                id: item.id,
                title: item.title ?? "",
              }
              chatRowMenuRef.current?.toggle(e)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                e.stopPropagation()
                menuActionRef.current = {
                  id: item.id,
                  title: item.title ?? "",
                }
                chatRowMenuRef.current?.toggle(e)
              }
            }}
          >
            <i className="pi pi-ellipsis-v" aria-hidden />
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Menu
        ref={chatRowMenuRef}
        model={chatRowMenuItems}
        popup
        appendTo={typeof document !== "undefined" ? document.body : undefined}
        baseZIndex={1200}
        className="estimation-agent-quick-v3__history-chat-menu"
      />

      <ChatTitleModal
        visible={titleModalOpen}
        chatId={titleEditChatId}
        initialTitle={titleEditInitialTitle}
        onHide={() => {
          setTitleModalOpen(false)
          setTitleEditChatId(null)
          setTitleEditInitialTitle("")
        }}
        onSaved={handleTitleSaved}
      />

      <Modal
        visible={deleteModalOpen}
        onHide={() => {
          if (!deleteBusy) {
            setDeleteModalOpen(false)
            setDeleteChatId(null)
          }
        }}
        header="Eliminar chat"
        className="estimation-agent-quick-v3__history-delete-dialog"
        dismissableMask={!deleteBusy}
        closable={!deleteBusy}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              type="button"
              label="Cancelar"
              className="p-button-text"
              disabled={deleteBusy}
              onClick={() => {
                setDeleteModalOpen(false)
                setDeleteChatId(null)
              }}
            />
            <Button
              type="button"
              label="Eliminar"
              className="p-button-danger"
              loading={deleteBusy}
              onClick={() => handleConfirmDelete()}
            />
          </div>
        }
      >
        <p className="m-0">
          ¿Seguro que quieres eliminar este chat? Esta acción no se puede
          deshacer.
        </p>
      </Modal>

      <div
        className="estimation-agent-quick-v3__history-backdrop"
        onClick={onClose}
        role="presentation"
      />
      <aside
        className="estimation-agent-quick-v3__history-panel"
        aria-label="Chats"
      >
        <div className="estimation-agent-quick-v3__history-panel-head">
          <div className="estimation-agent-quick-v3__history-panel-title-row">
            <i
              className="pi pi-comments estimation-agent-quick-v3__history-panel-main-icon"
              aria-hidden
            />
            <h2 className="estimation-agent-quick-v3__history-panel-title">
              Chats
            </h2>
          </div>
          <Button
            type="button"
            icon="pi pi-times"
            className="p-button-rounded p-button-text"
            onClick={onClose}
            aria-label="Cerrar historial"
          />
        </div>

        {loadError ?
          <p className="estimation-agent-quick-v3__history-panel-error">
            Hubo un error al cargar los datos
          </p>
        : null}

        {!loadError ?
          <div className="estimation-agent-quick-v3__history-section">
            <div
              className="estimation-agent-quick-v3__history-section-heading"
              id="estimation-agent-quick-v3-history-recientes"
            >
              <i
                className="pi pi-history estimation-agent-quick-v3__history-section-icon"
                aria-hidden
              />
              <span className="estimation-agent-quick-v3__history-section-label">
                Recientes
              </span>
            </div>
            <ul
              className="estimation-agent-quick-v3__history-list"
              role="list"
              aria-labelledby="estimation-agent-quick-v3-history-recientes"
              aria-busy={loading}
            >
              {loading ?
                Array.from({ length: skeletonCount }, (_, i) => (
                  <li key={`history-skeleton-${i}`}>
                    <div
                      className="estimation-agent-quick-v3__history-item-skeleton"
                      aria-hidden
                    />
                  </li>
                ))
              : displayItems.map((item) => (
                  <li key={item.id}>{renderRowInner(item)}</li>
                ))
              }
            </ul>
          </div>
        : null}
      </aside>
    </>
  )
}
